'use client';

import { useJotaiCart } from '@/contexts/JotaiCartProvider';
import { cn } from '@/utils/cn';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function FloatingCartIcon() {
    const { items, isInitialized } = useJotaiCart();
    const [isAnimating, setIsAnimating] = useState(false);
    const [previousItemCount, setPreviousItemCount] = useState(0);

    // Calculate total quantity
    const totalQuantity = items.reduce((sum, item) => sum + item.qty, 0);

    // Trigger animation when items are added
    useEffect(() => {
        if (totalQuantity > previousItemCount && previousItemCount > 0) {
            setIsAnimating(true);
            const timer = setTimeout(() => setIsAnimating(false), 600);
            return () => clearTimeout(timer);
        }
        setPreviousItemCount(totalQuantity);
    }, [totalQuantity, previousItemCount]);

    // Don't show if cart isn't initialized or has no items
    if (!isInitialized || items.length === 0) {
        return null;
    }

    return (
        <div className="fixed right-4 bottom-20 z-50 font-neue-haas">
            <Link
                href="/cart"
                className={cn(
                    'relative flex items-center justify-center rounded-full bg-haug-lila-100 p-4 text-haug-ivory-100 shadow-lg',
                    'transition-all duration-200 hover:bg-haug-lila-200',
                    isAnimating && 'scale-110 animate-bounce'
                )}
            >
                {/* Shopping Cart Icon */}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <title>Shopping Cart</title>
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5M7 13v6a2 2 0 002 2h6a2 2 0 002-2v-6"
                    />
                    <circle cx="9" cy="20" r="1" />
                    <circle cx="20" cy="20" r="1" />
                </svg>

                {/* Quantity Badge */}
                {totalQuantity > 0 && (
                    <span
                        className={cn(
                            '-right-2 -top-2 absolute flex h-6 w-6 items-center justify-center rounded-full bg-haug-coral-100 font-bold text-haug-ivory-100 text-xs',
                            isAnimating && 'animate-pulse'
                        )}
                    >
                        {totalQuantity > 99 ? '99+' : totalQuantity}
                    </span>
                )}
            </Link>
        </div>
    );
}
